import { Injectable } from '@angular/core';
import { ShareableLinkType } from '../constants-types/Enums';
import { List } from '../constants-types/PiList.model';
import { FullPattern, Pattern } from '../core-data-model/Pattern.model';
import {
    Person,
    PizzaPlaygroundSessionPersonWithVisibility
} from '../core-data-model/Person.model';
import {
    PizzaPlaygroundSession,
    teamActionRecommendations
} from '../core-data-model/Strategy.model';
import {
    StrategySurveyQuestion,
    SelfServeTeamMemberServerResponse
} from '../core-data-model/StrategyApi.serverinterface';
import { UserUpdate } from '../core-data-model/User.model';
import { Util } from '../core-utilities/Util';
import { BehavioralAssessmentFactory } from '../object-factory/BehavioralAssessment.factory';
import {
    PizzaPlaygroundSessionServer,
    teamActionRecommendationsServer,
    teamSummaryShareLinkServer
} from './PizzaPlaygroundApi.serverinterface';

/*
The PizzaPlayground files are left in the core-services instead of DesignExplorer because these files are not exclusive to Design app anymore.
The Teams Api is currently consuming getPizzaPlaygroundSessionByTeamId and the list might grow further in the future.
Also, moving this file into @TeamPizza/DesingExplorer will introduce a cycle between TeamPizza <-> Core-Services or even Teams <-> TeamPizza
*/
@Injectable({
    providedIn: 'root'
})
export class PizzaPlaygroundApiTransformers {
    constructor(
        private util: Util,
        private baFactory: BehavioralAssessmentFactory
    ) {}

    transformStrategyPlaygroundSession(
        server: PizzaPlaygroundSessionServer
    ): PizzaPlaygroundSession {
        return new PizzaPlaygroundSession({
            sessionId: server.sessionId,
            sessionName: server.sessionName,
            personIdsWithColor: server.personIdsWithColor,
            selectedStrategyIds: server.selectedStrategyIds,
            createdDate: this.util.newDate(server.createdDate),
            modifiedDate: this.util.newDate(server.modifiedDate),
            objectiveType: server.objectiveType,
            selectedActivities: server.selectedActivities,
            strategyType: server.strategyType,
            teamType: server.teamType,
            teamId: server.teamId,
            teamFunctionType: server.teamFunctionType,
            otherTeamFunction: server.otherTeamFunction,
            crossFunctionId: server.crossFunctionId,
            teamImprovementIds: server.teamImprovementIds,
            otherTeamImprovement: server.otherTeamImprovement,
            isStrategyAutoSelectionEnabled:
                server.isStrategyAutoSelectionEnabled,
            teamActions: server.teamActions,
            customActions: server.customActions,
            actionTeamType: server.actionTeamType,
            shareLinkGuid: server.shareLinkGuid
        });
    }

    prepareTeamReport(
        session: PizzaPlaygroundSession,
        leadershipObjectives: StrategySurveyQuestion[],
        hydratedPeople: List<PizzaPlaygroundSessionPersonWithVisibility>
    ) {
        const report = {
            // Mapped to TeamSummaryReportCto.
            session: session,
            questions: leadershipObjectives,
            people: hydratedPeople
        };

        // Transform BehavioralAssessment data to map to backend data
        // objects by populating any missing readonly fields.
        report.people.forEach((p) => {
            const patternNumber = p.personObject._referencePattern?.id;
            p.personObject.behavioralAssessments.forEach(
                (assessment, index, collection) => {
                    collection[index] = this.baFactory.new_BehavioralAssessment(
                        {
                            sentDate: assessment.sentDate,
                            completedDate: assessment.completedDate,
                            expirationDate: assessment.expirationDate,
                            administeredBy: assessment.administeredBy,
                            administeredById: assessment.administeredById,
                            assessmentState: assessment.assessmentState,
                            id: assessment.id,
                            pattern: this.prepareFullPatternData(
                                assessment.pattern,
                                assessment.scoreId
                            ),
                            patternNumber: patternNumber,
                            scoreId: assessment.scoreId
                        }
                    );
                }
            );
        });

        return report;
    }

    private prepareFullPatternData(
        pattern: FullPattern,
        id: string
    ): FullPattern {
        let fullPattern;

        if (pattern) {
            fullPattern = new FullPattern();

            // Copy constructor does additional logic on E values. Setting this after instantiating
            // to circumvent this one-off logic, this is necessary since the FullPatternDataCto and
            // PatternDataCto do not have these fields as nullables and thus *must* be present in
            // order for this BehavioralAssessment to serialize in the request.
            fullPattern.self = new Pattern({
                A: pattern.self.A || 0,
                B: pattern.self.B || 0,
                C: pattern.self.C || 0,
                D: pattern.self.D || 0,
                E: pattern.self.E || 0,
                M: pattern.self.M
            });
            fullPattern.selfConcept = new Pattern({
                A: pattern.selfConcept.A || 0,
                B: pattern.selfConcept.B || 0,
                C: pattern.selfConcept.C || 0,
                D: pattern.selfConcept.D || 0,
                E: pattern.selfConcept.E || 0,
                M: pattern.selfConcept.M
            });
            fullPattern.synthesis = new Pattern({
                A: pattern.synthesis.A || 0,
                B: pattern.synthesis.B || 0,
                C: pattern.synthesis.C || 0,
                D: pattern.synthesis.D || 0,
                E: pattern.synthesis.E || 0,
                M: pattern.synthesis.M
            });
            fullPattern.scoreId = id;
        }

        return fullPattern;
    }

    tranformSelfServeTeamMemberFromPost(
        serverData: SelfServeTeamMemberServerResponse,
        teamMemberDetails: UserUpdate
    ): Person {
        return new Person(
            Object.assign({
                id: serverData,
                firstName: teamMemberDetails.firstName,
                lastName: teamMemberDetails.lastName,
                email: teamMemberDetails.email
            })
        );
    }

    transformTeamActionRecommendations(
        actionRecommendations: teamActionRecommendationsServer
    ): teamActionRecommendations {
        return {
            actionIds: actionRecommendations.actionIds
        };
    }

    transformTeamSummaryShareLinkToServer(
        companyId: string,
        redirectUrl: string,
        session: PizzaPlaygroundSession
    ): teamSummaryShareLinkServer {
        return {
            companyId: companyId,
            redirectUrl: redirectUrl,
            permissionsData: {
                sessionId: session.sessionId,
                sessionName: session.sessionName
            },
            linkType: ShareableLinkType.TeamSummary
        };
    }
}
